import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Users, UserPlus, Trash2, Mail, Clock, CheckCircle, XCircle, BarChart3, Crown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeamPage() {
  const { user, token, refreshUser } = useAuth();
  const [teamData, setTeamData] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [teamName, setTeamName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    fetchTeamData();
  }, [token]);

  const fetchTeamData = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/team`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTeamData(data);
      
      if (data.has_team && data.is_owner) {
        fetchUsageData();
      }
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/team/usage`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsageData(data);
      }
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!teamName.trim()) return;
    
    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/team/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: teamName })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Team succesvol aangemaakt!');
        setTeamName('');
        setShowCreateForm(false);
        fetchTeamData();
      } else {
        toast.error(data.detail || 'Kon team niet aanmaken');
      }
    } catch (error) {
      toast.error('Er ging iets mis');
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    
    setInviting(true);
    try {
      const response = await fetch(`${API_URL}/api/team/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: inviteEmail })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Uitnodiging verzonden naar ${inviteEmail}`);
        setInviteEmail('');
        fetchTeamData();
      } else {
        toast.error(data.detail || 'Kon uitnodiging niet verzenden');
      }
    } catch (error) {
      toast.error('Er ging iets mis');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (email) => {
    if (!window.confirm(`Weet je zeker dat je ${email} wilt verwijderen uit het team?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/team/member/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`${email} is verwijderd uit het team`);
        fetchTeamData();
        fetchUsageData();
      } else {
        toast.error(data.detail || 'Kon teamlid niet verwijderen');
      }
    } catch (error) {
      toast.error('Er ging iets mis');
    }
  };

  const handleLeaveTeam = async () => {
    if (!window.confirm('Weet je zeker dat je het team wilt verlaten? Je verliest toegang tot Team features.')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/team/leave`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Je hebt het team verlaten');
        refreshUser();
        fetchTeamData();
      } else {
        toast.error(data.detail || 'Kon team niet verlaten');
      }
    } catch (error) {
      toast.error('Er ging iets mis');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
      </div>
    );
  }

  // User doesn't have Team plan
  if (user?.plan !== 'team') {
    return (
      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <div className="bg-card border border-border rounded-2xl p-8 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-muted" />
          <h1 className="text-2xl font-bold mb-2">Team Management</h1>
          <p className="text-muted mb-6">
            Team management is alleen beschikbaar voor Team plan gebruikers.
          </p>
          <a
            href="/pricing"
            className="inline-flex items-center gap-2 bg-accent text-white px-6 py-3 rounded-xl font-medium hover:bg-accent/90 transition-colors"
          >
            Upgrade naar Team
          </a>
        </div>
      </div>
    );
  }

  // User has Team plan but no team yet
  if (!teamData?.has_team) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <div className="bg-card border border-border rounded-2xl p-8">
          <div className="text-center mb-8">
            <Users className="w-16 h-16 mx-auto mb-4 text-accent" />
            <h1 className="text-2xl font-bold mb-2">Maak je team aan</h1>
            <p className="text-muted">
              Je hebt een Team abonnement. Maak een team aan om leden uit te nodigen.
            </p>
          </div>
          
          {showCreateForm ? (
            <form onSubmit={handleCreateTeam} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Teamnaam</label>
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Bijv. Accountantskantoor De Vries"
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent"
                  required
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 px-4 py-3 border border-border rounded-xl hover:bg-muted/10 transition-colors"
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-accent text-white px-4 py-3 rounded-xl font-medium hover:bg-accent/90 transition-colors disabled:opacity-50"
                >
                  {creating ? 'Aanmaken...' : 'Team aanmaken'}
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full bg-accent text-white px-6 py-3 rounded-xl font-medium hover:bg-accent/90 transition-colors flex items-center justify-center gap-2"
            >
              <UserPlus className="w-5 h-5" />
              Team aanmaken
            </button>
          )}
        </div>
      </div>
    );
  }

  const team = teamData.team;
  const isOwner = teamData.is_owner;

  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl">
      {/* Team Header */}
      <div className="bg-card border border-border rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center">
              <Users className="w-7 h-7 text-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{team.name}</h1>
              <p className="text-muted text-sm">
                {isOwner ? 'Jij bent de eigenaar' : `Eigenaar: ${team.owner_email}`}
              </p>
            </div>
          </div>
          {!isOwner && (
            <button
              onClick={handleLeaveTeam}
              className="text-red-500 hover:text-red-600 text-sm font-medium"
            >
              Team verlaten
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Team Members */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-accent" />
            Teamleden ({team.members.length + 1})
          </h2>
          
          <div className="space-y-3">
            {/* Owner */}
            <div className="flex items-center justify-between p-3 bg-accent/5 rounded-xl border border-accent/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center">
                  <Crown className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="font-medium">{isOwner ? 'Jij' : team.owner_email}</p>
                  <p className="text-xs text-muted">Eigenaar</p>
                </div>
              </div>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            
            {/* Members */}
            {team.members.map((member) => (
              <div key={member.email} className="flex items-center justify-between p-3 bg-muted/5 rounded-xl border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-muted/20 rounded-full flex items-center justify-center">
                    {member.status === 'pending' ? (
                      <Clock className="w-5 h-5 text-yellow-500" />
                    ) : (
                      <Mail className="w-5 h-5 text-muted" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{member.email}</p>
                    <p className="text-xs text-muted">
                      {member.status === 'pending' ? 'Uitnodiging verstuurd' : 'Actief lid'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {member.status === 'active' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <Clock className="w-5 h-5 text-yellow-500" />
                  )}
                  {isOwner && (
                    <button
                      onClick={() => handleRemoveMember(member.email)}
                      className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Verwijderen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Invite Form (Owner only) */}
          {isOwner && (
            <form onSubmit={handleInvite} className="mt-4 pt-4 border-t border-border">
              <label className="block text-sm font-medium mb-2">Nieuw teamlid uitnodigen</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="email@voorbeeld.nl"
                  className="flex-1 px-4 py-2 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-accent text-sm"
                  required
                />
                <button
                  type="submit"
                  disabled={inviting}
                  className="bg-accent text-white px-4 py-2 rounded-xl font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  {inviting ? '...' : 'Uitnodigen'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Usage Statistics (Owner only) */}
        {isOwner && usageData && (
          <div className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-accent" />
              Gebruik deze maand
            </h2>
            
            {/* Total Usage */}
            <div className="bg-accent/5 rounded-xl p-4 mb-4 border border-accent/20">
              <p className="text-sm text-muted mb-1">Totaal teamgebruik</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-2xl font-bold">{usageData.total_usage.decodes}</p>
                  <p className="text-xs text-muted">Decoderingen</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{usageData.total_usage.bulk}</p>
                  <p className="text-xs text-muted">Bulk checks</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{usageData.total_usage.csv}</p>
                  <p className="text-xs text-muted">CSV uploads</p>
                </div>
              </div>
            </div>
            
            {/* Per Member Usage */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted">Per teamlid</p>
              {usageData.members.map((member) => (
                <div key={member.email} className="flex items-center justify-between p-3 bg-muted/5 rounded-xl">
                  <div className="flex items-center gap-2">
                    {member.is_owner && <Crown className="w-4 h-4 text-accent" />}
                    <span className="text-sm font-medium truncate max-w-[150px]">{member.name}</span>
                    {member.status === 'pending' && (
                      <span className="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-0.5 rounded-full">
                        Pending
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted">
                    <span title="Decoderingen">{member.usage.decodes} dec</span>
                    <span title="Bulk">{member.usage.bulk} bulk</span>
                    <span title="CSV">{member.usage.csv} csv</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info for non-owners */}
        {!isOwner && (
          <div className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Je bent teamlid
            </h2>
            <p className="text-muted text-sm mb-4">
              Als teamlid heb je toegang tot alle Team plan features:
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Onbeperkte decoderingen
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Bulk decoder (tot 500 per keer)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Bank CSV/TXT upload
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Tekst scanner
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                PDF naar MT940 conversie
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Export naar CSV/Excel
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
